/* LT-28 — Reusable price history panel.
 *
 * Renders an entity's ledger newest-first, with plain-English change source
 * labels and UK-formatted dates + GBP values. Also emits a plain-text
 * summary above the list (D6: no sparklines; recharts remains untouched).
 *
 * Props:
 *   entityType : 'library_item' | 'material' | 'labour_rate'
 *   entityId   : uuid string
 *   fieldLabel : e.g. 'rate' (library) | 'price' (material) | 'hourly rate' (labour)
 *
 * States:
 *   Loading    — skeleton
 *   Empty      — muted "No price history yet"
 *   With entries — trend summary + list
 */
import React, { useEffect, useState } from "react";
import { Clock, TrendingUp, TrendingDown, Minus, RefreshCw } from "lucide-react";

import { api } from "../lib/api";
import { gbp, ukDateTime } from "../lib/format";

const cls = (...a) => a.filter(Boolean).join(" ");

// Plain-English labels for `change_source`. Kept lowercase per house style.
const SOURCE_LABEL = {
  manual_edit:      "manual edit",
  recipe_edit:      "recipe edit",
  cascade_accepted: "cascade accepted",
  promotion:        "promoted from draft",
  bulk_uplift:      "bulk uplift",     // reserved (LT-30)
  import:           "import",          // LT-19b — the ledger names the batch beside it
  import_rollback:  "import rollback", // LT-19c — an import walked back, new → old
};

const SOURCE_TONE = {
  manual_edit:      { bg: "#E8EFE9", fg: "#2F4F3E", border: "#D6E2D8" },
  recipe_edit:      { bg: "#EEF1F7", fg: "#2F3E52", border: "#D8DFE8" },
  cascade_accepted: { bg: "#FEF3E2", fg: "#7A5C1A", border: "#F0D9A8" },
  promotion:        { bg: "#F1EBFA", fg: "#4B2F73", border: "#DECDF3" },
  bulk_uplift:      { bg: "#F8F1DF", fg: "#7A5C1A", border: "#F0D9A8" },
  import:           { bg: "#E6F1F7", fg: "#1F4B63", border: "#CFE0EB" },
  import_rollback:  { bg: "#F3EEE6", fg: "#6B4A1F", border: "#E4D6C2" },
};

function SourcePill({ source }) {
  const tone = SOURCE_TONE[source] || SOURCE_TONE.manual_edit;
  return (
    <span
      className="inline-flex items-center rounded font-mono uppercase"
      style={{
        padding: "1px 6px",
        fontSize: 9,
        fontWeight: 600,
        letterSpacing: "0.05em",
        background: tone.bg, color: tone.fg,
        border: `1px solid ${tone.border}`,
      }}
    >
      {SOURCE_LABEL[source] || source}
    </span>
  );
}

function DeltaCell({ delta_abs, delta_pct }) {
  if (delta_abs == null) {
    return <span className="text-ink-4" style={{ fontSize: 11.5 }}>—</span>;
  }
  const up = delta_abs > 0;
  const down = delta_abs < 0;
  const flat = delta_abs === 0;
  const Icon = up ? TrendingUp : down ? TrendingDown : Minus;
  return (
    <span
      className={cls(
        "inline-flex items-center gap-1 font-mono tnum",
        up ? "text-note-red" : down ? "text-note-green" : "text-ink-4"
      )}
      style={{ fontSize: 12 }}
    >
      <Icon size={11} />
      {up ? "+" : ""}{gbp(delta_abs)}
      {delta_pct != null && !flat && (
        <span className="text-ink-3" style={{ fontSize: 10.5 }}>
          ({delta_pct > 0 ? "+" : ""}{delta_pct.toFixed(2)}%)
        </span>
      )}
    </span>
  );
}

export function PriceHistoryPanel({ entityType, entityId, fieldLabel = "rate", suppliersById = {} }) {
  const [entries, setEntries] = useState(null); // null = loading
  const [refreshingKey, setRefreshingKey] = useState(0);

  const load = async () => {
    if (!entityId) return;
    try {
      const r = await api.get(`/price-history/${entityType}/${entityId}`);
      setEntries(r.data || []);
    } catch (e) {
      setEntries([]);
    }
  };

  useEffect(() => { load(); }, [entityType, entityId, refreshingKey]);   // eslint-disable-line

  // Header summary
  const count = entries?.length ?? 0;
  const latest = entries?.[0];
  const earliest = entries?.[entries.length - 1];
  const summary = (() => {
    // D7: fewer than 2 entries → muted "No history yet"
    if (count < 2) return null;
    const from = earliest?.new_value;
    const to = latest?.new_value;
    if (from == null || to == null) return null;
    return `${gbp(from)} → ${gbp(to)} over ${count} change${count === 1 ? "" : "s"}`;
  })();

  return (
    <div className="rounded-md border border-line bg-surface" data-testid={`price-history-panel-${entityType}`}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-line-soft">
        <div className="flex items-center gap-2">
          <Clock size={13} className="text-ink-3" />
          <span className="font-display text-ink" style={{ fontSize: 12.5, fontWeight: 600 }}>
            Price history
          </span>
          {count > 0 && (
            <span className="font-mono tnum text-ink-3" style={{ fontSize: 11 }}>
              · {count} entr{count === 1 ? "y" : "ies"}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setRefreshingKey((k) => k + 1)}
          className="text-ink-3 hover:text-ink"
          data-testid="price-history-refresh"
          aria-label="Refresh"
          title="Refresh"
        >
          <RefreshCw size={11} />
        </button>
      </div>

      {entries === null ? (
        <div className="px-3 py-6 text-center text-ink-3" style={{ fontSize: 11.5 }}>
          Loading history…
        </div>
      ) : count === 0 ? (
        <div className="px-3 py-6 text-center" data-testid="price-history-empty">
          <span className="text-ink-4 italic" style={{ fontSize: 11.5 }}>
            No price history yet
          </span>
        </div>
      ) : (
        <>
          {summary && (
            <div className="px-3 py-2 border-b border-line-soft bg-surface-2/40">
              <span className="text-ink-2 font-mono tnum" style={{ fontSize: 11.5 }}>
                Trend · {summary}
              </span>
            </div>
          )}
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-line-soft bg-surface-2/50">
                <th className="col-head px-3 py-2" style={{ width: 130 }}>Date</th>
                <th className="col-head px-3 py-2" style={{ width: 90 }}>Source</th>
                <th className="col-head px-3 py-2" style={{ width: 140 }}>Supplier</th>
                <th className="col-head px-3 py-2 text-right" style={{ width: 100 }}>Old</th>
                <th className="col-head px-3 py-2 text-right" style={{ width: 100 }}>New</th>
                <th className="col-head px-3 py-2 text-right">Delta</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-b border-line-soft last:border-b-0" data-testid={`price-history-row-${e.id}`}>
                  <td className="px-3 py-2 font-mono tnum text-ink-3" style={{ fontSize: 11 }}>
                    {ukDateTime(e.changed_at)}
                  </td>
                  <td className="px-3 py-2">
                    <SourcePill source={e.change_source} />
                    {/* LT-19b §9 — provenance; entries written before LT-19b have neither field (F4) */}
                    {e.import_batch_id && (
                      <a href={`/materials?tab=imports&batch=${e.import_batch_id}`} className="ml-1.5 text-ink-3 hover:text-ink underline font-mono"
                         style={{ fontSize: 10 }} data-testid={`price-history-batch-${e.id}`}>
                        batch {String(e.import_batch_id).slice(0, 8)}{e.effective_from ? ` · effective ${e.effective_from}` : ""}
                      </a>
                    )}
                  </td>
                  <td className="px-3 py-2 text-ink-2" style={{ fontSize: 11.5 }}>
                    {e.supplier_id ? (suppliersById[e.supplier_id]?.name || <span className="text-ink-4 italic">unknown supplier</span>) : <span className="text-ink-4">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tnum text-ink-3" style={{ fontSize: 11.5 }}>
                    {e.old_value == null ? "—" : gbp(e.old_value)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tnum text-ink" style={{ fontSize: 12, fontWeight: 500 }}>
                    {gbp(e.new_value)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <DeltaCell delta_abs={e.delta_abs} delta_pct={e.delta_pct} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
